import { useEffect, useState } from "react";
import { Loader2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

const Badge = ({ children, tone }) => (
  <span className={`text-xs font-bold px-2.5 py-1 rounded-sm uppercase tracking-wide ${tone}`}>{children}</span>
);

const FRIENDLY_STATUS = {
  new: { label: "Received", tone: "bg-brand-ice/30 text-blue-900" },
  contacted: { label: "We reached out", tone: "bg-amber-100 text-amber-800" },
  quoted: { label: "Quote sent — check your phone/email", tone: "bg-purple-100 text-purple-800" },
  won: { label: "Booked! We're on it", tone: "bg-green-100 text-green-800" },
  closed: { label: "Closed", tone: "bg-zinc-100 text-zinc-600" },
};

const friendly = (status) => FRIENDLY_STATUS[status] || { label: status, tone: "bg-zinc-100 text-zinc-600" };

export const MyRequests = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/account/requests").then((r) => setData(r.data)).catch(() => setData({ leads: [], estimates: [], commercial_plans: [] }));
  }, []);

  const items = data
    ? [
        ...data.leads.map((l) => ({ kind: "Service Request", tone: "bg-brand-subtle text-brand", title: l.service || "General AC Help", detail: l.message, status: l.status, created_at: l.created_at, key: `lead-${l.id}` })),
        ...data.estimates.map((e) => ({ kind: "Replacement Estimate", tone: "bg-brand-ice/30 text-blue-900", title: `${e.square_footage} sq ft · ${e.floors} floor(s)${e.preferred_brand ? ` · ${e.preferred_brand}` : ""}`, detail: e.message, status: e.status, created_at: e.created_at, key: `est-${e.id}` })),
        ...data.commercial_plans.map((c) => ({ kind: "Commercial Plan", tone: "bg-red-100 text-brand-red", title: `${c.business_name} · ${c.service_frequency} service`, detail: c.concerns, status: c.status, created_at: c.created_at, key: `cp-${c.id}` })),
      ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    : null;

  let body;
  if (items === null) {
    body = <Loader2 className="w-6 h-6 text-brand animate-spin" />;
  } else if (items.length === 0) {
    body = (
      <p data-testid="my-requests-empty" className="text-sm text-zinc-600">
        No requests yet. Estimates, service requests and plan inquiries you send us will appear here.
      </p>
    );
  } else {
    body = (
      <ul className="divide-y divide-zinc-100">
        {items.map((it) => (
          <li key={it.key} data-testid={`my-request-${it.key}`} className="py-4 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Badge tone={it.tone}>{it.kind}</Badge>
                <span className="text-xs text-zinc-500">{it.created_at?.slice(0, 10)}</span>
              </span>
              <Badge tone={friendly(it.status).tone}>{friendly(it.status).label}</Badge>
            </div>
            <p className="font-medium text-brand-ink mt-2">{it.title}</p>
            {it.detail && <p className="text-sm text-zinc-600 mt-0.5">"{it.detail}"</p>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div data-testid="my-requests-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <h2 className="font-heading font-bold text-lg text-brand-ink mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-brand" /> My Requests &amp; Messages
      </h2>
      {body}
    </div>
  );
};
